import { useEffect, useRef } from "react";

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: Record<string, unknown>) => string;
      remove: (id: string) => void;
    };
    onTurnstileLoad?: () => void;
  }
}

/** Site key from the deployed config. Empty means this install does not use the check. */
export function turnstileSiteKey(): string {
  const runtime = typeof window !== "undefined" ? window.__BACKSTORY_CONFIG__?.turnstileSiteKey : "";
  return (runtime || (import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined) || "").trim();
}

let loading: Promise<void> | null = null;

function loadTurnstile(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  if (loading) return loading;
  loading = new Promise((resolve) => {
    window.onTurnstileLoad = () => resolve();
    const script = document.createElement("script");
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onTurnstileLoad&render=explicit";
    script.async = true;
    document.head.appendChild(script);
  });
  return loading;
}

/** The check shown before an anonymous request a bot could abuse. */
export function Turnstile({ onToken }: { onToken: (token: string) => void }) {
  const host = useRef<HTMLDivElement>(null);
  const onTokenRef = useRef(onToken);
  onTokenRef.current = onToken;
  const siteKey = turnstileSiteKey();

  useEffect(() => {
    if (!siteKey || !host.current) return;
    let id = "";
    let gone = false;
    void loadTurnstile().then(() => {
      if (gone || !host.current || !window.turnstile) return;
      id = window.turnstile.render(host.current, {
        sitekey: siteKey,
        theme: "auto",
        callback: (token: string) => onTokenRef.current(token),
        "expired-callback": () => onTokenRef.current(""),
        "error-callback": () => onTokenRef.current(""),
      });
    });
    return () => {
      gone = true;
      if (id && window.turnstile) window.turnstile.remove(id);
    };
  }, [siteKey]);

  if (!siteKey) return null;
  return <div ref={host} className="turnstile" />;
}
